/* eslint-disable no-magic-numbers */
import {
  DEFAULT_TILE_SIZE,
  EXTENT_PADDING_MULTIPLICATOR,
  OPTIONS,
  ZOOM_RESCALING_FACTOR,
} from '@/constants/map';
import { MapSize } from '@/redux/map/map.types';
import { Point } from '@/types/map';
import { Coordinate } from 'ol/coordinate';
import { Extent, boundingExtent } from 'ol/extent';
import { ViewOptions } from 'ol/View';

type PointToProjection = (point: Point) => Coordinate;

/**
 * Computes the OpenLayers view extent for a diagram of the given size. Shared by
 * every map that renders the diagram so they pan within identical bounds.
 */
export const getMapExtent = (mapSize: MapSize, pointToProjection: PointToProjection): Extent => {
  let adjustRatio = 1;
  if (typeof window !== 'undefined') {
    adjustRatio = mapSize.width / mapSize.height / (window.screen.width / window.screen.height);
  }
  let extentPadding;
  if (adjustRatio > 1) {
    extentPadding = {
      horizontal: mapSize.width * EXTENT_PADDING_MULTIPLICATOR,
      vertical: mapSize.height * EXTENT_PADDING_MULTIPLICATOR * adjustRatio,
    };
  } else {
    extentPadding = {
      horizontal: (mapSize.width * EXTENT_PADDING_MULTIPLICATOR) / adjustRatio,
      vertical: mapSize.height * EXTENT_PADDING_MULTIPLICATOR,
    };
  }

  const topLeftPoint: Point = {
    x: mapSize.width + extentPadding.horizontal,
    y: mapSize.height + extentPadding.vertical,
  };

  const bottomRightPoint: Point = {
    x: -extentPadding.horizontal,
    y: -extentPadding.vertical,
  };

  return boundingExtent([topLeftPoint, bottomRightPoint].map(pointToProjection));
};

interface GetMapViewConfigInput {
  mapSize: MapSize;
  center: Coordinate;
  zoom: number | undefined;
  extent: Extent;
}

/** OpenLayers view options plus the custom `originalMaxZoom` prop the app reads back. */
export type MapViewOptions = ViewOptions & { originalMaxZoom: number };

/**
 * Builds the OpenLayers {@link ViewOptions} for a diagram view. The zoom scaling
 * (`zoomFactor`, `originalMaxZoom`, `min`/`maxZoom`) is identical across every
 * map rendering the diagram, which keeps the resolution-based element cutoff
 * (see `isFeatureFilled`) consistent between the main map and the loupe. Only
 * `center` and `zoom` differ per map.
 */
export const getMapViewConfig = ({
  mapSize,
  center,
  zoom,
  extent,
}: GetMapViewConfigInput): MapViewOptions => ({
  center,
  zoom,
  showFullExtent: OPTIONS.showFullExtent,
  zoomFactor: 2 ** (1 / ZOOM_RESCALING_FACTOR),
  // originalMaxZoom is a custom prop read elsewhere (cutoff math, view-scale guard)
  originalMaxZoom: mapSize.maxZoom * ZOOM_RESCALING_FACTOR,
  maxZoom:
    mapSize.width < 1.6 * DEFAULT_TILE_SIZE || mapSize.height < 1.6 * DEFAULT_TILE_SIZE
      ? Math.max(15, mapSize.maxZoom * ZOOM_RESCALING_FACTOR)
      : mapSize.maxZoom * ZOOM_RESCALING_FACTOR,
  minZoom: mapSize.minZoom * ZOOM_RESCALING_FACTOR,
  extent,
});
